import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const GAP = 8; // px between trigger and tooltip

function getCoords(triggerRect, tooltipRect, position) {
  let top, left;

  if (position === "top") {
    top = triggerRect.top - tooltipRect.height - GAP;
    left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  } else if (position === "bottom") {
    top = triggerRect.bottom + GAP;
    left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  } else if (position === "left") {
    top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    left = triggerRect.left - tooltipRect.width - GAP;
  } else {
    top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    left = triggerRect.right + GAP;
  }

  // Clamp to viewport
  left = Math.max(4, Math.min(left, window.innerWidth - tooltipRect.width - 4));
  top = Math.max(4, Math.min(top, window.innerHeight - tooltipRect.height - 4));

  return { top, left };
}

function resolvePosition(triggerRect, tooltipRect, preferred) {
  // Flip vertically if not enough space
  if (preferred === "top" && triggerRect.top - tooltipRect.height - GAP < 4) {
    return "bottom";
  }
  if (preferred === "bottom" && triggerRect.bottom + tooltipRect.height + GAP > window.innerHeight - 4) {
    return "top";
  }
  if (preferred === "left" && triggerRect.left - tooltipRect.width - GAP < 4) {
    return "right";
  }
  if (preferred === "right" && triggerRect.right + tooltipRect.width + GAP > window.innerWidth - 4) {
    return "left";
  }
  return preferred;
}

const Tooltip = ({ children, content, position = "top", borderColor = "#FFE100" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) return;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const resolved = resolvePosition(triggerRect, tooltipRect, position);
    setCoords(getCoords(triggerRect, tooltipRect, resolved));
  }, [position]);

  useEffect(() => {
    if (isVisible) updatePosition();
  }, [isVisible, updatePosition]);

  const tooltipStyle = {
    position: "fixed",
    top: coords.top,
    left: coords.left,
    zIndex: 99999,
    borderColor: borderColor,
    boxShadow: `0 0 12px ${borderColor}30`,
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}

      {isVisible && content && createPortal(
        <div
          ref={tooltipRef}
          className="px-3 py-2 text-sm font-medium bg-black text-white rounded-lg whitespace-nowrap border pointer-events-none
                     transition-opacity duration-200 ease-in-out opacity-100"
          style={tooltipStyle}
          role="tooltip"
        >
          <span className="inline-block">{content}</span>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
